/**
 * Платежи в Polygon через EIP-1193 провайдер, который отдаёт Reown AppKit
 * (MetaMask, Trust, WalletConnect и т.д.). Без wagmi-хуков: у нас нет
 * WagmiProvider в дереве, а один raw-провайдер здесь проще и предсказуемее.
 */

import { encodeFunctionData, parseUnits } from 'viem';
import { NETWORKS, TOKEN_CONTRACTS, EvmNetwork } from '../constants/networks';

export interface Eip1193Provider {
  request(args: { method: string; params?: unknown[] | object }): Promise<unknown>;
}

export interface EvmPayParams {
  network: EvmNetwork;
  from: string;
  to: string;
  amount: number;
  /** Нативная монета сети (POL / BNB / ETH) или USDT. */
  currency: string;
}

const ERC20_TRANSFER_ABI = [
  {
    type: 'function',
    name: 'transfer',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'to', type: 'address' },
      { name: 'value', type: 'uint256' },
    ],
    outputs: [{ name: '', type: 'bool' }],
  },
] as const;

function toHex(value: bigint): `0x${string}` {
  return `0x${value.toString(16)}` as `0x${string}`;
}

/** Переключить кошелёк на нужную EVM-сеть (Polygon / BNB Chain / Ethereum). */
export async function ensureEvmChain(provider: Eip1193Provider, network: EvmNetwork): Promise<void> {
  const evm = NETWORKS[network].evm;
  if (!evm) throw new Error('Not an EVM network: ' + network);
  const current = (await provider.request({ method: 'eth_chainId' })) as string;
  if (parseInt(current, 16) === evm.chainId) {
    return;
  }
  const chainIdHex = toHex(BigInt(evm.chainId));
  try {
    await provider.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: chainIdHex }] });
  } catch (err: any) {
    // 4902 — сеть не добавлена в кошелёк.
    if (err?.code === 4902 || /unrecognized chain|not added/i.test(err?.message || '')) {
      await provider.request({
        method: 'wallet_addEthereumChain',
        params: [
          {
            chainId: chainIdHex,
            chainName: evm.chainName,
            nativeCurrency: { name: evm.nativeName, symbol: evm.nativeName, decimals: 18 },
            rpcUrls: [evm.rpcUrl],
            blockExplorerUrls: [evm.explorer],
          },
        ],
      });
      return;
    }
    throw err;
  }
}

/** Совместимость: старое имя. */
export const ensurePolygonChain = (provider: Eip1193Provider) => ensureEvmChain(provider, 'polygon');

/** Отправить нативную монету или токен сети (USDT, USDC) на адрес платформы. Возвращает хеш транзакции. */
export async function payEvm(provider: Eip1193Provider, params: EvmPayParams): Promise<string> {
  if (!/^0x[0-9a-fA-F]{40}$/.test(params.to)) {
    throw new Error('Invalid recipient address');
  }
  if (!(params.amount > 0)) {
    throw new Error('Invalid amount');
  }

  await ensureEvmChain(provider, params.network);

  const native = NETWORKS[params.network].nativeToken;
  let tx: Record<string, string>;
  if (params.currency === native) {
    tx = {
      from: params.from,
      to: params.to,
      value: toHex(parseUnits(params.amount.toString(), 18)),
    };
  } else if ((TOKEN_CONTRACTS[params.network] as Record<string, { address: string; decimals: number }>)[params.currency]) {
    // Любой токен сети из реестра (USDT, USDC): ERC-20 transfer на адрес платформы.
    const token = (TOKEN_CONTRACTS[params.network] as Record<string, { address: string; decimals: number }>)[params.currency];
    const data = encodeFunctionData({
      abi: ERC20_TRANSFER_ABI,
      functionName: 'transfer',
      args: [params.to as `0x${string}`, parseUnits(params.amount.toString(), token.decimals)],
    });
    tx = { from: params.from, to: token.address, data, value: '0x0' };
  } else {
    throw new Error('Unsupported currency for ' + params.network + ': ' + params.currency);
  }

  try {
    const hash = (await provider.request({ method: 'eth_sendTransaction', params: [tx] })) as string;
    if (!hash || !/^0x[0-9a-fA-F]{64}$/.test(hash)) {
      throw new Error('Wallet returned no transaction hash');
    }
    return hash;
  } catch (err: any) {
    if (err?.code === 4001 || /reject|denied|cancel/i.test(err?.message || '')) {
      throw new Error('Transaction cancelled by user');
    }
    throw err instanceof Error ? err : new Error(String(err));
  }
}
